import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { fetcher, parseApiError } from '../lib/api';
import { getBackendUrl } from '../lib/backend';
import { useUser, SUBSCRIPTION_PLANS } from '../contexts/AgentContext';
import PlanCheckout from '../components/checkout/PlanCheckout';

// Feature component to display features with checkmarks or crosses
const FeatureItem: React.FC<{
  feature: string;
  included: boolean;
  isDuration?: boolean;
}> = ({ feature, included, isDuration }) => {
  // Don't show duration feature for free plan
  if (isDuration) return null;

  return (
    <li className="flex items-center">
      {included ? (
        <svg
          className="w-5 h-5 text-green-500 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 13l4 4L19 7"
          ></path>
        </svg>
      ) : (
        <svg
          className="w-5 h-5 text-red-500 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      )}
      <span className={`text-gray-700 ${!included ? 'line-through opacity-70' : ''}`}>
        {feature}
      </span>
    </li>
  );
};

// Plan Badge component
const PlanBadge: React.FC<{ planId: string; name: string; isExpired?: boolean }> = ({
  planId,
  name,
  isExpired,
}) => {
  return (
    <span
      className={`inline-block px-4 py-1 rounded-full text-sm ${
        planId === 'free'
          ? isExpired
            ? 'bg-red-200 text-red-800'
            : 'bg-yellow-200 text-yellow-800'
          : planId === 'professional'
            ? 'bg-primary text-white'
            : 'bg-purple-600 text-white'
      }`}
    >
      {name}
      {planId === 'free' && !isExpired && <span className="ml-1">• 14 días</span>}
      {planId === 'free' && isExpired && <span className="ml-1">• Expirado</span>}
    </span>
  );
};

// Subscription Card component
const SubscriptionCard: React.FC<{
  plan: typeof SUBSCRIPTION_PLANS.free;
  isCurrentPlan: boolean;
  onSubscribe: () => void;
  onChangePlan: () => void;
  loading: boolean;
  isLoggedIn: boolean;
  isExpired: boolean;
  isCancelled: boolean;
  isPaidPlan?: boolean;
}> = ({
  plan,
  isCurrentPlan,
  onSubscribe,
  onChangePlan,
  loading,
  isLoggedIn,
  isExpired,
  isCancelled,
  isPaidPlan = false,
}) => {
  // All features that should be included in any plan
  const allFeatures = [
    'Mensajes de chat ilimitados',
    'Minutos de voz ilimitados',
    'Mensajes de WhatsApp ilimitados',
    'Línea telefónica',
  ];

  // Check if a feature is included in this plan
  const isFeatureIncluded = (feature: string) => {
    return plan.features.some((f) => f.includes(feature));
  };

  // Check if a feature is about duration
  const isDurationFeature = (feature: string) => {
    return feature.includes('Duración');
  };

  // Determine if user needs to resubscribe (expired or cancelled)
  const needsResubscribe = isExpired || isCancelled;

  return (
    <div
      className={`bg-white rounded-lg shadow-lg p-6 flex flex-col h-full transform transition-all duration-300 hover:shadow-xl relative ${
        isCurrentPlan && !needsResubscribe ? 'border-2 border-primary scale-105' : 'hover:scale-105'
      } ${isExpired ? 'opacity-90 border-2 border-red-300' : ''} 
      ${isCancelled ? 'opacity-90 border-2 border-yellow-300' : ''}`}
    >
      {isExpired && (
        <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          Expirado
        </div>
      )}
      {isCancelled && (
        <div className="absolute -top-3 -right-3 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          Cancelado
        </div>
      )}
      <div className="text-center mb-4">
        <PlanBadge planId={plan.id} name={plan.name} isExpired={isExpired} />
      </div>
      <h3 className="text-xl font-bold mb-2 text-center">{plan.name}</h3>
      <p className="text-2xl font-semibold mb-4 text-center">
        ${plan.price}
        <span className="text-sm text-gray-600">{plan.id === 'free' ? '' : '/mes'}</span>
        <span className="text-xs text-gray-500 block mt-1">USD</span>
      </p>
      <p className="text-gray-600 mb-4 text-center">{plan.description}</p>
      {isExpired && !isPaidPlan && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-center text-sm">
          Tu período de prueba ha expirado. Actualiza ahora para seguir disfrutando de todas las
          funcionalidades.
        </div>
      )}
      {isExpired && isPaidPlan && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-center text-sm">
          Tu suscripción ha expirado. Renueva ahora para seguir disfrutando de todas las
          funcionalidades.
        </div>
      )}
      {isCancelled && isPaidPlan && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-2 rounded mb-4 text-center text-sm">
          Tu suscripción ha sido cancelada. Reactiva ahora para seguir disfrutando de todas las
          funcionalidades.
        </div>
      )}
      <div className="flex-grow">
        <p className="font-semibold mb-2 text-center">Características:</p>
        <ul className="space-y-2">
          {/* Show plan features */}
          {plan.features.map((feature, index) => (
            <FeatureItem
              key={index}
              feature={feature}
              included={true}
              isDuration={isDurationFeature(feature) && plan.id === 'free'}
            />
          ))}

          {/* Show missing features */}
          {allFeatures
            .filter((feature) => !isFeatureIncluded(feature))
            .map((feature, index) => (
              <FeatureItem key={`missing-${index}`} feature={feature} included={false} />
            ))}
        </ul>
      </div>
      <div className="mt-6">
        {!isLoggedIn ? (
          <button
            className="w-full bg-gray-300 text-gray-600 py-2 px-4 rounded-full cursor-not-allowed text-center"
            disabled
          >
            Inicia sesión para suscribirte
          </button>
        ) : isCurrentPlan && needsResubscribe ? (
          <button
            className="w-full bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-center font-medium shadow-md"
            onClick={onSubscribe}
            disabled={loading}
          >
            {isExpired ? 'Renovar Ahora' : 'Reactivar Ahora'}
          </button>
        ) : isCurrentPlan ? (
          <button
            className="w-full border-2 border-primary text-primary py-2 px-4 rounded-full cursor-not-allowed text-center font-medium"
            disabled
          >
            Plan Actual
          </button>
        ) : (
          <button
            className="w-full bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-center font-medium shadow-md"
            onClick={onSubscribe}
            disabled={loading}
          >
            {plan.id === 'free' ? 'Usar Plan Gratuito' : 'Cambiar a este Plan'}
          </button>
        )}
      </div>
    </div>
  );
};

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  features: string[];
}

interface Subscription {
  plan: string;
  status?: string;
  trialExpiry?: string | Date;
  currentPeriodEnd?: string | Date;
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // State for checkout modal
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);

  // Usar el contexto de usuario para obtener la suscripción
  const { user, getSubscriptionWithDetails } = useUser();
  const subscription = getSubscriptionWithDetails();

  // Obtener datos de suscripción para verificar si está cargando
  const { data: subscriptionData, error: subscriptionError } = useSWR(
    `${getBackendUrl()}/api/subscription`,
    fetcher,
  );

  const isLoading = !subscriptionData && !subscriptionError;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, isLoading, navigate]);

  // Check if subscription is cancelled
  const isSubscriptionCancelled = () => {
    if (!subscription) {
      return false;
    }

    return subscription.status === 'cancelled';
  };

  // Check if trial has expired - only for free plan
  const isTrialExpired = () => {
    if (!subscription || subscription.plan !== 'free') {
      return false;
    }

    // For free plan, check trial expiry
    if (subscription.trialExpiry) {
      const trialEnd = new Date(subscription.trialExpiry);
      const today = new Date();
      return trialEnd < today;
    }

    return false;
  };

  // Check if paid subscription has expired
  const isSubscriptionExpired = () => {
    if (!subscription || subscription.plan === 'free') {
      return false;
    }

    // For paid plans, check currentPeriodEnd
    if (subscription.currentPeriodEnd) {
      const periodEnd = new Date(subscription.currentPeriodEnd);
      const today = new Date();
      return periodEnd < today;
    }

    return false;
  };

  const handleSubscribe = async (planId: string) => {
    // If it's a free plan, use the existing flow
    if (planId === 'free') {
      try {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        // Create subscription
        const response = await fetch(`${getBackendUrl()}/api/subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ planId }),
        });

        if (!response.ok) {
          throw await parseApiError(response, 'No se pudo crear la suscripción');
        }

        await mutate(`${getBackendUrl()}/api/subscription`);

        setSuccessMessage(
          `¡Te has suscrito exitosamente al plan ${SUBSCRIPTION_PLANS[planId as keyof typeof SUBSCRIPTION_PLANS].name}!`,
        );
      } catch (err) {
        console.error('Error al suscribir:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    } else {
      // For paid plans, open the checkout modal
      const selectedPlan = SUBSCRIPTION_PLANS[planId as keyof typeof SUBSCRIPTION_PLANS];
      setCheckoutPlan(selectedPlan);
    }
  };

  const handleChangePlan = async (planId: string) => {
    // If it's a free plan, use the existing flow
    if (planId === 'free') {
      try {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        // Update subscription
        const response = await fetch(`${getBackendUrl()}/api/subscription`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ planId }),
        });

        if (!response.ok) {
          throw await parseApiError(response, 'No se pudo actualizar la suscripción');
        }

        await mutate(`${getBackendUrl()}/api/subscription`);

        setSuccessMessage(`¡Has cambiado exitosamente al plan ${SUBSCRIPTION_PLANS[planId as keyof typeof SUBSCRIPTION_PLANS].name}!`);
      } catch (err) {
        console.error('Error al cambiar plan:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    } else {
      // For paid plans, open the checkout modal for changing plan
      const selectedPlan = SUBSCRIPTION_PLANS[planId as keyof typeof SUBSCRIPTION_PLANS];
      setCheckoutPlan(selectedPlan);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      if (!window.confirm('¿Estás seguro de que deseas cancelar tu suscripción?')) {
        return;
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(`${getBackendUrl()}/api/subscription`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw await parseApiError(response, 'No se pudo cancelar la suscripción');
      }

      await mutate(`${getBackendUrl()}/api/subscription`);

      setSuccessMessage('Tu suscripción ha sido cancelada.');
    } catch (err) {
      console.error('Error al cancelar suscripción:', err);
      setError('No se pudo cancelar la suscripción. Por favor intenta nuevamente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // If still loading or redirecting, show loading state
  if (isLoading || loading || !user) {
    return (
      <div className="flex flex-col items-center justify-center my-8 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-gray-600">Cargando información de suscripción...</p>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="max-w-6xl mx-auto my-8 px-4">
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6"
          role="alert"
        >
          <p>Error al cargar los datos de suscripción. Por favor intenta nuevamente más tarde.</p>
        </div>
      </div>
    );
  }

  // Calcular días restantes para el plan gratuito o para planes pagos
  const getDaysRemaining = () => {
    if (!subscription) {
      return null;
    }

    // Use trial expiry for free plan, currentPeriodEnd for paid plans
    const endDate =
      subscription.plan === 'free'
        ? subscription.trialExpiry
          ? new Date(subscription.trialExpiry)
          : null
        : subscription.currentPeriodEnd
          ? new Date(subscription.currentPeriodEnd)
          : null;

    if (!endDate) return null;

    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = getDaysRemaining();
  const trialExpired = isTrialExpired();
  const subscriptionExpired = isSubscriptionExpired();

  // Filter plans based on current subscription
  const filteredPlans = Object.values(SUBSCRIPTION_PLANS).filter((plan) => {
    // If user is on free plan, show all plans
    if (subscription?.plan === 'free') {
      return true;
    }
    // If user is on paid plan, don't show free plan
    return plan.id !== 'free';
  });

  return (
    <div className="max-w-6xl mx-auto my-8 px-4">
      <h1 className="text-3xl font-bold text-primary mb-8 text-center">
        Administración de Suscripción
      </h1>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 max-w-md mx-auto"
          role="alert"
        >
          <p>{error}</p>
        </div>
      )}

      {successMessage && (
        <div
          className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 max-w-md mx-auto"
          role="alert"
        >
          <p>{successMessage}</p>
        </div>
      )}

      {/* Alerta de suscripción expirada */}
      {subscription &&
        ((subscription.plan === 'free' && trialExpired) ||
          (subscription.plan !== 'free' && subscriptionExpired)) && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 max-w-2xl mx-auto shadow-md rounded-r">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {subscription.plan === 'free'
                    ? 'Tu período de prueba ha expirado'
                    : 'Tu suscripción ha expirado'}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    {subscription.plan === 'free'
                      ? 'Para seguir disfrutando de todas las funcionalidades, actualiza ahora a un plan de pago.'
                      : 'Para seguir disfrutando de todas las funcionalidades, renueva tu suscripción.'}
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors shadow-sm"
                    onClick={() =>
                      subscription.plan === 'free'
                        ? handleChangePlan('professional')
                        : handleChangePlan(subscription.plan)
                    }
                  >
                    {subscription.plan === 'free' ? 'Actualizar Ahora' : 'Renovar Ahora'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Información de suscripción actual */}
      {subscription && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-10 max-w-md mx-auto transform hover:scale-105 transition-transform duration-300">
          <h2 className="text-2xl font-bold mb-4 text-center text-primary">Suscripción Actual</h2>
          <div className="mb-4">
            <p className="font-semibold text-lg text-center">
              {subscription.planDetails.name}
              {subscription.plan === 'free' && daysRemaining !== null && !trialExpired && (
                <span className="ml-2 text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  {daysRemaining} días restantes
                </span>
              )}
              {subscription.plan === 'free' && trialExpired && (
                <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full">
                  Expirado
                </span>
              )}
              {subscription.plan !== 'free' && daysRemaining !== null && !subscriptionExpired && (
                <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  {daysRemaining} días restantes
                </span>
              )}
              {subscription.plan !== 'free' && subscriptionExpired && (
                <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full">
                  Expirado
                </span>
              )}
            </p>
            <p className="text-gray-700 text-center">
              Estado:{' '}
              <span className="capitalize">
                {subscription.plan === 'free'
                  ? subscription.status === 'active' && !trialExpired
                    ? 'Activa'
                    : 'Inactiva'
                  : subscription.status === 'active' && !subscriptionExpired
                    ? 'Activa'
                    : 'Inactiva'}
              </span>
            </p>
            {(subscription.trialExpiry || subscription.currentPeriodEnd) && (
              <p className="text-gray-700 text-center">
                {subscription.plan === 'free'
                  ? trialExpired
                    ? 'Expiró el'
                    : 'Vence el'
                  : subscriptionExpired
                    ? 'Expiró el'
                    : 'Vence el'}
                :{' '}
                {new Date(
                  subscription.plan === 'free'
                    ? subscription.trialExpiry || ''
                    : subscription.currentPeriodEnd || '',
                ).toLocaleDateString('es-ES')}
              </p>
            )}
          </div>
          {subscription.status === 'active' &&
            subscription.plan !== 'free' &&
            !subscriptionExpired && (
              <div className="text-center">
                <button
                  className="bg-red-500 hover:bg-red-600 text-white py-2 px-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  onClick={handleCancelSubscription}
                  disabled={loading}
                >
                  Cancelar Suscripción
                </button>
              </div>
            )}
          {subscription.plan === 'free' && trialExpired && (
            <div className="text-center mt-4">
              <p className="text-red-600 mb-2">
                Tu período de prueba ha expirado. Necesitas actualizar a un plan de pago para seguir
                disfrutando de todas las funcionalidades.
              </p>
              <button
                className="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                onClick={() => handleChangePlan('professional')}
                disabled={loading}
              >
                Renovar Ahora
              </button>
            </div>
          )}
          {subscription.plan !== 'free' && subscriptionExpired && (
            <div className="text-center mt-4">
              <p className="text-red-600 mb-2">
                Tu suscripción ha expirado. Renueva ahora para seguir disfrutando de todas las
                funcionalidades.
              </p>
              <button
                className="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                onClick={() => handleChangePlan(subscription.plan)}
                disabled={loading}
              >
                Renovar Ahora
              </button>
            </div>
          )}
        </div>
      )}

      {/* Planes disponibles */}
      <h2 className="text-2xl font-bold mb-8 text-center text-primary">Planes Disponibles</h2>
      {filteredPlans.length === 2 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:max-w-4xl mx-auto">
          {filteredPlans.map((plan) => (
            <SubscriptionCard
              key={plan.id}
              plan={plan}
              isCurrentPlan={subscription?.plan === plan.id}
              onSubscribe={() => handleSubscribe(plan.id)}
              onChangePlan={() => handleChangePlan(plan.id)}
              loading={loading}
              isLoggedIn={!!user}
              isExpired={
                (plan.id === 'free' && trialExpired) ||
                (plan.id !== 'free' && subscription?.plan === plan.id && subscriptionExpired)
              }
              isCancelled={plan.id !== 'free' && subscription?.plan === plan.id && isSubscriptionCancelled()}
              isPaidPlan={plan.id !== 'free'}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {filteredPlans.map((plan) => (
            <SubscriptionCard
              key={plan.id}
              plan={plan}
              isCurrentPlan={subscription?.plan === plan.id}
              onSubscribe={() => handleSubscribe(plan.id)}
              onChangePlan={() => handleChangePlan(plan.id)}
              loading={loading}
              isLoggedIn={!!user}
              isExpired={
                (plan.id === 'free' && trialExpired) ||
                (plan.id !== 'free' && subscription?.plan === plan.id && subscriptionExpired)
              }
              isCancelled={plan.id !== 'free' && subscription?.plan === plan.id && isSubscriptionCancelled()}
              isPaidPlan={plan.id !== 'free'}
            />
          ))}
        </div>
      )}

      {checkoutPlan && (
        <PlanCheckout
          planId={checkoutPlan.id}
          planName={checkoutPlan.name}
          planPrice={checkoutPlan.price}
          onCancel={() => setCheckoutPlan(null)}
          isChangingPlan={subscription?.plan !== 'free' && subscription?.plan !== undefined}
          onClose={() => {
            // Refresh data when the modal is closed
            mutate(`${getBackendUrl()}/api/subscription`);
          }}
        />
      )}
    </div>
  );
};

export default Subscription;
